import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { listBandLabels, stageInvitations } from '@/lib/admin/people-api';
import { TEMPLATE_COLUMNS } from '@/lib/admin/people-template';
import { Loader2, Plus } from 'lucide-react';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const ADD_NEW = '__add_new__';
const NONE = '__none__';

export function AddPersonDialog({ orgId, open, onOpenChange }: { orgId: string; open: boolean; onOpenChange: (v: boolean) => void }) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [email, setEmail] = useState('');
  const [fields, setFields] = useState<Record<string, string>>({});
  // Tracks dimensions where admin chose "Add new..." so we render a free-text input
  const [customMode, setCustomMode] = useState<Record<string, boolean>>({});

  const { data: bandLabels = [] } = useQuery({
    queryKey: ['admin', 'band-labels', orgId],
    queryFn: () => listBandLabels(orgId),
    enabled: open && !!orgId,
  });

  const labelsByDim = useMemo(() => {
    const m: Record<string, string[]> = {};
    for (const b of bandLabels) {
      if (!m[b.dimension]) m[b.dimension] = [];
      m[b.dimension].push(b.label);
    }
    return m;
  }, [bandLabels]);

  const reset = () => {
    setEmail('');
    setFields({});
    setCustomMode({});
  };

  const mut = useMutation({
    mutationFn: async () => {
      const bands = TEMPLATE_COLUMNS.filter((c) => c !== 'email')
        .map((c) => ({ dimension: c, label: (fields[c] ?? '').trim() }))
        .filter((b) => b.label.length > 0);
      return stageInvitations(orgId, [{ email: email.trim().toLowerCase(), bands }]);
    },
    onSuccess: (res) => {
      const r = res?.[0];
      toast({
        title: r?.status === 'merged' ? 'Bands merged' : 'Invitation staged',
        description: r?.status === 'merged'
          ? 'This person already exists; new bands were added.'
          : 'They will be matched on first sign-in.',
      });
      qc.invalidateQueries({ queryKey: ['admin', 'people', orgId] });
      qc.invalidateQueries({ queryKey: ['admin', 'pending', orgId] });
      qc.invalidateQueries({ queryKey: ['admin', 'band-labels', orgId] });
      reset();
      onOpenChange(false);
    },
    onError: (e: any) => toast({ title: 'Failed', description: e?.message ?? 'Could not stage.', variant: 'destructive' }),
  });

  const emailValid = EMAIL_RE.test(email.trim());

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add a person</DialogTitle>
          <DialogDescription>
            Pick from existing values to keep departments and teams consistent. Choose "Add new…" only when truly needed.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
          <div>
            <Label htmlFor="ap-email">Email <span className="text-destructive">*</span></Label>
            <Input id="ap-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="person@company.com" />
          </div>
          {TEMPLATE_COLUMNS.filter((c) => c !== 'email').map((c) => {
            const opts = labelsByDim[c] ?? [];
            const isCustom = customMode[c] || (fields[c] && !opts.includes(fields[c]));
            const value = fields[c] ?? '';

            return (
              <div key={c}>
                <Label htmlFor={`ap-${c}`} className="capitalize">{c.replace('_', ' ')}</Label>
                {isCustom ? (
                  <div className="flex gap-2">
                    <Input
                      id={`ap-${c}`}
                      value={value}
                      onChange={(e) => setFields((p) => ({ ...p, [c]: e.target.value }))}
                      placeholder={opts.length ? 'New value (will create a new band)' : 'Enter value'}
                      autoFocus
                    />
                    {opts.length > 0 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setCustomMode((p) => ({ ...p, [c]: false }));
                          setFields((p) => ({ ...p, [c]: '' }));
                        }}
                      >
                        Cancel
                      </Button>
                    )}
                  </div>
                ) : (
                  <Select
                    value={value || NONE}
                    onValueChange={(v) => {
                      if (v === ADD_NEW) {
                        setCustomMode((p) => ({ ...p, [c]: true }));
                        setFields((p) => ({ ...p, [c]: '' }));
                      } else if (v === NONE) {
                        setFields((p) => ({ ...p, [c]: '' }));
                      } else {
                        setFields((p) => ({ ...p, [c]: v }));
                      }
                    }}
                  >
                    <SelectTrigger id={`ap-${c}`}>
                      <SelectValue placeholder={opts.length ? 'Select…' : 'No existing values'} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>— None —</SelectItem>
                      {opts.map((label) => (
                        <SelectItem key={label} value={label}>{label}</SelectItem>
                      ))}
                      <SelectItem value={ADD_NEW}>
                        <span className="flex items-center gap-1 text-primary">
                          <Plus className="h-3 w-3" /> Add new…
                        </span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>
            );
          })}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button disabled={!emailValid || mut.isPending} onClick={() => mut.mutate()}>
            {mut.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Stage invitation
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
